import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ClashTestDetails, ClashSettings } from "@ServerTypes/clashTests/clashTests.schema";

/**
 * `users` is NOT re-declared here any more. It used to be `users?: unknown[]` because
 * `clashTestDetailsSchema` did not describe the folder-permission roster the route returns on every
 * row; the schema now declares it (`clashTestUserSchema`), so the typed member is inherited from
 * `ClashTestDetails` and `ModuleUsersAccess` receives it already shaped as `{ id, role, permissions }`.
 * Re-declaring it was also a one-way door: an interface member may only be narrowed, so `unknown[]`
 * blocked the schema from ever declaring a typed array.
 */
export interface ClashDetailsState extends ClashTestDetails {
  isFetch: boolean;
  /**
   * Widened with an explicit `| undefined` to match the base member: `ClashTestDetails.settings` is
   * `clashSettingsSchema.optional()`, i.e. `ClashSettings | undefined`. Under
   * `exactOptionalPropertyTypes` a bare `settings?: ClashSettings` would NARROW the inherited member
   * to "key absent", which the `{ ...action.payload }` spread in `setClashDetails` cannot satisfy.
   */
  settings?: ClashSettings | undefined;
}

const clashDetailsSlice = createSlice({
  name: "clashDetails",
  initialState: { isFetch: false } as ClashDetailsState,
  reducers: {
    setClashDetails: (_state: ClashDetailsState, action: PayloadAction<ClashTestDetails>) => ({
      ...action.payload,
      isFetch: true,
    }),
    setClashSettings: (_state: ClashDetailsState, action: PayloadAction<Partial<ClashSettings>>) => ({
      ..._state,
      settings: { ..._state.settings, ...action.payload },
    }),
    resetClashDetails: (_state: ClashDetailsState, _action: PayloadAction<void>) => ({ isFetch: false }),
  },
});

const { setClashSettings: _setClashSettingsRaw } = clashDetailsSlice.actions;

// `setClashDetails` is intentionally exported raw (NOT wrapped with `Exact<>`).
// The consumer in actions/clash.ts uses `_.get(response, "data.result", {})`
// which yields `ClashTestDetails | {}` — the `{}` branch fails Exact.
// `resetClashDetails` has a `void` payload so it's exported raw too.
export const { setClashDetails, resetClashDetails } = clashDetailsSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setClashSettings = <T extends Exact<Partial<ClashSettings>, T>>(payload: T) =>
  _setClashSettingsRaw(payload);

export const clashDetails = clashDetailsSlice.reducer;

export default {
  clashDetails,
};
